// ===================================================================
// THEME PROVIDER - Design System Portfolio
// ===================================================================
// Gestion du thème light/dark avec persistance localStorage
// Toggle binaire simple pour meilleure UX
// ===================================================================

'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';

// Types pour le thème - simplifié en binaire
export type Theme = 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Clé localStorage pour la persistance
const THEME_STORAGE_KEY = 'portfolio-theme';

// Détecte la préférence système (utilisé uniquement pour le défaut initial)
const getSystemPreference = (): Theme => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

// Récupère le thème sauvegardé ou la préférence système
const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'light';
  const saved = localStorage.getItem(THEME_STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') {
    return saved;
  }
  // Par défaut, utilise la préférence système
  return getSystemPreference();
};

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({ children, defaultTheme }: ThemeProviderProps) {
  // État : thème actuel
  const [theme, setThemeState] = useState<Theme>(() => defaultTheme || 'light');
  const [mounted, setMounted] = useState(false);

  // Initialisation côté client uniquement (setState intentionnel : sync hydration-safe)

  useEffect(() => {
    const initialTheme = defaultTheme || getInitialTheme();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setThemeState(initialTheme);

    setMounted(true);
  }, [defaultTheme]);

  // Applique le thème au document quand il change
  useEffect(() => {
    if (!mounted) return;

    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);

    // Sauvegarde dans localStorage
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme, mounted]);

  // Fonction pour définir le thème - mémorisée
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
  }, []);

  // Toggle simple light <-> dark - mémorisé
  const toggleTheme = useCallback(() => {
    setThemeState(prev => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  // Valeur du contexte mémorisée pour éviter les re-renders
  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolvedTheme: theme,
      setTheme,
      toggleTheme,
    }),
    [theme, setTheme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

// Hook personnalisé pour utiliser le thème
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeProvider;
